import React, { useEffect, useState } from 'react';
import PainelDoRateio from './PainelDoRateio';
import PainelDoMotor from './PainelDoMotor';
import ListasDoRateio, { HistoricoDasListas } from './ListasDoRateio';
import AplicarAsCotas from './AplicarAsCotas';
import { PortaDoComecarADividir } from './ComecarADividir';
import CascaDeGaveta from './shared/CascaDeGaveta';
import { useRateio } from '../hooks/useRateio';
import { rateioService } from '../services/api';
import { ComRateio, TEXTO_DO_CRITERIO, TEXTO_DA_BASE, fmtPercentual } from '../utils/rateioDoGrupo';
import { EstadoDoMotor } from '../utils/motorDoRateio';
import { montarPainelDoGrupo } from '../utils/quemUsaQuemPaga';
import { resumoDoRateio } from '../utils/resumoDoRateio';
import { precisaAplicar } from '../utils/aplicarCotas';
import { fmtCompetencia } from '../utils/formatters';
import { mensagemDoErroDeCarga } from '../design';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';

/**
 * O RATEIO DO GRUPO, em Grupos Econômicos (gaveta do rateio, 10/10/2026; desenho aprovado pelo Maestro).
 *
 * O grupo aberto mostrava tudo de uma vez, e a divisão DUAS vezes ("Quem usa, quem paga" e "Como está sendo
 * dividida" eram a mesma tabela por empresa), com o texto do motor no meio. Agora o grupo tem UMA linha de resumo e
 * o botão "Rateio", que abre a gaveta com as abas Divisão, Motor (admin), Listas e Histórico. Grupo sem rateio: no
 * lugar da linha, o "Começar a dividir". Nada de comportamento muda: as mesmas chamadas, o mesmo cache (useRateio), a
 * mesma chave de remontagem (`versao`), os mesmos componentes de gravação.
 */

const brl = (v: number) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface Comum {
  clientId: number;
  contracts: any[];
  clients: any[];
  nome: (clientId: number) => string;
  /** Sobe quando o rateio é gravado: remonta os painéis para lerem a versão nova. */
  versao: number;
  ehAdmin: boolean;
}

/** O estado do motor é ADMIN (GET /rateio/motor). Sem admin, ou sem leitura, fica null: a linha omite. */
function useEstadoDoMotor(clientId: number, ligado: boolean): EstadoDoMotor | null {
  const [estado, setEstado] = useState<EstadoDoMotor | null>(null);
  useEffect(() => {
    if (!ligado) { setEstado(null); return; }
    let vivo = true;
    rateioService.estadoDoMotor(clientId).then(
      (e: EstadoDoMotor) => { if (vivo) setEstado(e); },
      () => { if (vivo) setEstado(null); },
    );
    return () => { vivo = false; };
  }, [clientId, ligado]);
  return estado;
}

/** A linha de resumo do rateio do grupo, com o botão que abre a gaveta. */
export function LinhaDoRateio({ clientId, ehAdmin, onAbrir }: { clientId: number; ehAdmin: boolean; onAbrir: () => void }) {
  const { rateio, erro, carregando } = useRateio(clientId);
  const comRateio = !!rateio && rateio.temRateio !== false;
  const motor = useEstadoDoMotor(clientId, ehAdmin && comRateio);
  if (!rateio) {
    if (erro) return <p className={`text-sm ${TONE_TEXT.warning}`} data-rateio='erro'>Não consegui ler o rateio do grupo: {mensagemDoErroDeCarga(erro)}</p>;
    return carregando ? <p className='text-sm text-slate-500' data-rateio='lendo'>Lendo o rateio do grupo…</p> : null;
  }
  if (!comRateio) return null;
  const r = resumoDoRateio(rateio as ComRateio, motor);
  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-sm ${TONE_BORDER.group} ${TONE_SURFACE.group}`} data-rateio='resumo'>
      <span className={`px-1.5 py-0.5 rounded text-xs font-medium uppercase tracking-wide ${TONE_BADGE.group}`}>rateio</span>
      <span className='text-slate-200'>{r.partes.join(' · ')}</span>
      {r.alertas.map((a) => (
        <span key={a.texto} className={`text-xs ${a.tom === 'danger' ? TONE_TEXT.danger : TONE_TEXT.warning}`}>⚠ {a.texto}</span>
      ))}
      <button type='button' onClick={onAbrir} className={`ml-auto text-xs px-3 py-1.5 rounded-lg border ${TONE_BORDER.group} ${TONE_TEXT.group} hover:text-white`}>
        Rateio →
      </button>
    </div>
  );
}

/** A divisão numa tabela só: o que era "Quem usa, quem paga" e "Como está sendo dividida". */
export function DivisaoDoRateio({ clientId, contracts, clients, nome, ehAdmin, onRedividir }: Omit<Comum, 'versao'> & { onRedividir: () => void }) {
  const { rateio } = useRateio(clientId);
  if (!rateio || rateio.temRateio === false) return null;
  const rr = rateio as ComRateio;
  const p = montarPainelDoGrupo(rr, contracts, clients);
  if (!p) return null;
  const linha = (id: number) => rr.linhas.find((l) => l.clientId === id);
  const porRamais = rr.criterio === 'ramais_contratados';
  return (
    <div className='space-y-3' data-rateio='divisao'>
      <div className='flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm'>
        <span className='text-slate-300'>
          {TEXTO_DO_CRITERIO[rr.criterio]}, {TEXTO_DA_BASE[rr.baseDoPercentual]}{rr.competenciaInicio ? ` · desde ${fmtCompetencia(rr.competenciaInicio)}` : ''}
        </span>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium tabular-nums ${TONE_BADGE.neutral}`}>
          {p.ramais} {p.ramais === 1 ? 'ramal' : 'ramais'} · {p.ddrs} DDR
        </span>
        <span className='ml-auto text-xs text-slate-400 tabular-nums'>
          compartilhado <b className='text-slate-200'>{brl(p.pool)}</b> · MRR do grupo <b className='text-green-400'>{brl(p.mrr)}</b>
        </span>
      </div>
      {p.divergente && (
        <div className={`flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 text-sm ${TONE_SURFACE.warning} ${TONE_BORDER.warning}`} role='status'>
          <span className={`flex-1 min-w-0 ${TONE_TEXT.warning}`}>A base mudou. A divisão vigente foi acordada com outra contagem de ramais.</span>
          {ehAdmin && (
            <button type='button' onClick={onRedividir} className='px-3 py-1.5 rounded-lg border border-slate-600 bg-slate-800 text-sm text-slate-200 hover:bg-slate-700'>
              Redividir
            </button>
          )}
        </div>
      )}
      <div className='rounded-lg border border-slate-700 overflow-x-auto'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='text-xs text-slate-400'>
              <th className='text-left font-medium px-3 py-2'>Empresa</th>
              {porRamais && <th className='text-right font-medium px-3 py-2'>Ramais</th>}
              <th className='text-right font-medium px-3 py-2'>DDR</th>
              <th className='px-3 py-2' />
              <th className='text-right font-medium px-3 py-2'>Acordado</th>
              {p.divergente && <th className='text-right font-medium px-3 py-2'>Hoje daria</th>}
              <th className='text-right font-medium px-3 py-2'>Cota acordada</th>
              <th className='text-right font-medium px-3 py-2'>MRR</th>
            </tr>
          </thead>
          <tbody>
            {p.empresas.map((e) => {
              const l = linha(e.clientId);
              const acordado = l?.valorAcordado ?? null;
              const cobraDiferente = acordado != null && Math.round(acordado * 100) !== Math.round(e.valor * 100);
              return (
                <tr key={e.clientId} className='border-t border-slate-800'>
                  <td className='px-3 py-2 text-slate-200 whitespace-nowrap'>
                    {nome(e.clientId)}{e.ehMatriz && <span className='ml-1.5 text-[11px] text-slate-500'>· matriz</span>}
                  </td>
                  {porRamais && <td className='px-3 py-2 text-right tabular-nums whitespace-nowrap text-slate-300'>{e.ramais}</td>}
                  <td className='px-3 py-2 text-right tabular-nums whitespace-nowrap text-slate-300'>{e.ddrs}</td>
                  <td className='px-3 py-2'>
                    <span className='block w-28 h-1.5 rounded bg-slate-700 overflow-hidden'>
                      <span className='block h-full bg-violet-400' style={{ width: `${Math.max(0, Math.min(100, e.percentual))}%` }} />
                    </span>
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${TONE_TEXT.group}`}>{fmtPercentual(e.percentual)}</td>
                  {p.divergente && <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${TONE_TEXT.warning}`}>{fmtPercentual(e.percentualCalculado)}</td>}
                  <td className='px-3 py-2 text-right tabular-nums whitespace-nowrap text-green-400'>
                    {/* Sem cota acordada (versão antiga, sem o valor em reais), o número é "—": o cobrado vai embaixo, dito
                        como cobrado. Mostrar o cobrado sob o título de acordado seria um número sem fonte. */}
                    {acordado != null ? brl(acordado) : <span className='text-slate-500'>—</span>}
                    {(cobraDiferente || acordado == null) && <div className={`text-[11px] ${acordado == null ? 'text-slate-400' : TONE_TEXT.warning}`}>cobra hoje {brl(e.valor)}</div>}
                  </td>
                  <td className='px-3 py-2 text-right tabular-nums whitespace-nowrap text-slate-300'>{brl(e.mrr)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className='border-t border-slate-700 bg-green-500/5 font-medium'>
              <td className='px-3 py-2 text-slate-200'>Total</td>
              {porRamais && <td className='px-3 py-2 text-right tabular-nums whitespace-nowrap text-slate-300'>{p.ramais}</td>}
              <td className='px-3 py-2 text-right tabular-nums whitespace-nowrap text-slate-300'>{p.ddrs}</td>
              <td className='px-3 py-2' />
              <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${rr.somaVigenteFecha ? 'text-green-400' : 'text-red-300'}`}>{fmtPercentual(rr.somaVigente)}</td>
              {p.divergente && <td className='px-3 py-2' />}
              <td className='px-3 py-2 text-right tabular-nums whitespace-nowrap text-green-400'>
                {rr.linhas.every((l) => l.valorAcordado != null)
                  ? brl(rr.linhas.reduce((t, l) => t + (l.valorAcordado || 0), 0))
                  : <><span className='text-slate-500'>—</span><div className='text-[11px] font-normal text-slate-400'>compartilhado {brl(p.pool)}</div></>}
              </td>
              <td className='px-3 py-2 text-right tabular-nums whitespace-nowrap text-green-400'>{brl(p.mrr)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {rr.observacao && <p className='text-xs text-slate-500 whitespace-pre-line'>{rr.observacao}</p>}
    </div>
  );
}

/** O atalho do "Aplicar as cotas" na aba Motor (a casa dele segue sendo o Gravar das Listas). */
function AtalhoDoAplicar({ clientId, contracts, nome, onAplicado }: { clientId: number; contracts: any[]; nome: (id: number) => string; onAplicado: () => void }) {
  const { rateio } = useRateio(clientId);
  const [aberto, setAberto] = useState(false);
  const [aplicou, setAplicou] = useState(false);
  if (!rateio || rateio.temRateio === false) return null;
  const rr = rateio as ComRateio;
  const precisa = precisaAplicar(rr as any);
  return (
    <div className='mt-3 flex items-center gap-3 rounded-lg border border-slate-700 px-3 py-2 text-sm' data-rateio='atalho-aplicar'>
      <span className='flex-1 text-slate-300'>
        {precisa ? 'Há contratos que não carregam a cota acordada: o motor recusaria essas faturas.' : 'Os contratos carregam a cota acordada.'}
      </span>
      {precisa && (
        <button type='button' onClick={() => setAberto(true)} className='shrink-0 text-xs px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200'>
          Aplicar as cotas nos contratos
        </button>
      )}
      {aberto && (
        <AplicarAsCotas rateio={rr as any} contracts={contracts as any} nome={nome} onClose={() => { setAberto(false); if (aplicou) onAplicado(); }} onAplicado={() => setAplicou(true)} />
      )}
    </div>
  );
}

type Aba = 'divisao' | 'motor' | 'listas' | 'historico';

/** A gaveta do rateio: Divisão, Motor (admin), Listas e Histórico. */
export function GavetaDoRateio({ clientId, contracts, clients, nome, versao, ehAdmin, onClose, onRedividir, onListasGravadas, onAplicado, abaInicial = 'divisao', abrirAplicarAoLer = false }: Comum & {
  onClose: () => void;
  onRedividir: () => void;
  onListasGravadas: () => void;
  onAplicado: () => void;
  abaInicial?: Aba;
  /** Recém-saído do "Começar a dividir": a aba Listas abre o "Aplicar as cotas" quando o rateio novo chega. */
  abrirAplicarAoLer?: boolean;
}) {
  const [aba, setAba] = useState<Aba>(abaInicial);
  const { rateio } = useRateio(clientId);
  const abas = [
    { chave: 'divisao', rotulo: 'Divisão' },
    ...(ehAdmin ? [{ chave: 'motor', rotulo: 'Motor' }] : []),
    { chave: 'listas', rotulo: 'Listas' },
    { chave: 'historico', rotulo: 'Histórico' },
  ];
  return (
    <CascaDeGaveta titulo={`Rateio · ${nome(clientId)}`} rotulo='Rateio do grupo' onClose={onClose} fecharAoClicarFora largura='ficha' abas={abas} abaAtiva={aba} onAba={(c) => setAba(c as Aba)}>
      <div className='p-4'>
        {aba === 'divisao' && (
          <DivisaoDoRateio key={`divisao-${clientId}-${versao}`} clientId={clientId} contracts={contracts} clients={clients} nome={nome} ehAdmin={ehAdmin} onRedividir={onRedividir} />
        )}
        {aba === 'motor' && ehAdmin && (
          <>
            <PainelDoMotor key={`motor-${clientId}-${versao}`} clientId={clientId} nome={nome} />
            <AtalhoDoAplicar key={`aplicar-${clientId}-${versao}`} clientId={clientId} contracts={contracts} nome={nome} onAplicado={onAplicado} />
          </>
        )}
        {aba === 'listas' && (
          <ListasDoRateio key={`listas-${clientId}-${versao}`} clientId={clientId} contracts={contracts} nome={nome} podeEditar={ehAdmin}
            onGravado={onListasGravadas} abrirAplicarAoLer={abrirAplicarAoLer} onAplicado={onAplicado} semHistorico />
        )}
        {aba === 'historico' && (
          <div className='space-y-4'>
            {rateio && rateio.temRateio !== false && <HistoricoDasListas rateio={rateio as ComRateio} contracts={contracts} />}
            <PainelDoRateio key={`rateio-${clientId}-${versao}`} clientId={clientId} nome={nome} aguardarEscolha />
          </div>
        )}
      </div>
    </CascaDeGaveta>
  );
}

/** O rateio dentro do grupo aberto: a linha de resumo (ou o "Começar a dividir") e a gaveta. */
export function RateioDoGrupo({ clientId, contracts, clients, nome, versao, ehAdmin, abrirAplicarAoLer, onRedividir, onListasGravadas, onAplicado, onPrimeiraDivisaoGravada }: Comum & {
  abrirAplicarAoLer: boolean;
  onRedividir: () => void;
  onListasGravadas: () => void;
  onAplicado: () => void;
  onPrimeiraDivisaoGravada: () => void;
}) {
  const [aberta, setAberta] = useState<false | Aba>(false);
  // Recém-saído do "Começar a dividir": a gaveta abre nas Listas, onde o "Aplicar as cotas" abre sozinho, como antes.
  useEffect(() => { if (abrirAplicarAoLer) setAberta('listas'); }, [abrirAplicarAoLer]);
  return (
    <div className='px-4 pt-4 pl-12 space-y-2'>
      <LinhaDoRateio key={`linha-${clientId}-${versao}`} clientId={clientId} ehAdmin={ehAdmin} onAbrir={() => setAberta('divisao')} />
      {/* Grupo sem rateio: a primeira divisão (09/10/2026). O grupo como o backend mede: a matriz e quem tem
          parentClientId nela. Some sozinho quando o grupo já tem rateio. */}
      <PortaDoComecarADividir
        key={`comecar-${clientId}-${versao}`}
        clientId={clientId}
        empresas={[clientId, ...clients.filter((c) => c.parentClientId === clientId).map((c) => c.id)]}
        contracts={contracts}
        nome={nome}
        podeEditar={ehAdmin}
        onGravado={onPrimeiraDivisaoGravada}
      />
      {aberta && (
        <GavetaDoRateio clientId={clientId} contracts={contracts} clients={clients} nome={nome} versao={versao} ehAdmin={ehAdmin}
          abaInicial={aberta} abrirAplicarAoLer={abrirAplicarAoLer}
          onClose={() => setAberta(false)} onRedividir={onRedividir} onListasGravadas={onListasGravadas} onAplicado={onAplicado} />
      )}
    </div>
  );
}
